import { useState } from 'react';
import { EmptyState } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { PaymentMethod, Sale } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { variantLabel } from '../components/Operation';
import { useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

/** Ventas recientes de la ubicación, con devolución total o parcial ligada a la venta. */
export function SalesHistory({ sales, onChanged, methods = [] }: { sales: Sale[]; onChanged: () => void; methods?: PaymentMethod[] }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [sale, setSale] = useState<Sale | null>(null);
  const [qtys, setQtys] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState('');

  // El reembolso se sugiere según cómo se pagó la venta: una venta en efectivo propone "Efectivo" (y la caja
  // baja sola). Así "Cambio de producto" (sin devolver dinero) queda como una elección a propósito, no el valor
  // por defecto, que era lo que dejaba la caja por encima de lo real.
  const suggestedRefund = (s: Sale): string => {
    const cash = s.payments.find((p) => p.kind === 'CASH');
    if (cash) return cash.code;
    const other = s.payments.find((p) => ['TRANSFER', 'CARD'].includes(p.kind));
    return other?.code ?? '';
  };

  const open = (s: Sale) => {
    setSale(s);
    setQtys({});
    setReason('');
    setRefund(suggestedRefund(s));
  };

  const saleHadCash = sale?.payments.some((p) => p.kind === 'CASH') ?? false;

  const submit = () =>
    run(async () => {
      if (!sale) return;
      const lines = sale.lines.map((l) => ({ sale_line_id: l.sale_line_id, qty: Number(qtys[l.sale_line_id]) || 0 })).filter((l) => l.qty > 0);
      if (lines.length === 0) return show(t('returns.nothing'));
      const scope = `devolucion:${sale.id}`;
      try {
        const r = await api.createReturn(requestKey(scope), sale.id, { lines, reason, refund_method: refund });
        releaseKey(scope);
        setSale(null);
        show(t('returns.done', { doc: r.doc_no }));
        onChanged();
      } catch (e) {
        showError(e);
      }
    });

  return (
    <Panel title={t('salesHistory.title')} description={t('salesHistory.description')}>
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>{t('salesHistory.date')}</th>
              <th>{t('salesHistory.doc')}</th>
              <th className="num">{t('salesHistory.units')}</th>
              <th className="num">{t('salesHistory.total')}</th>
              <th>{t('salesHistory.payments')}</th>
              <th>{t('common.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody id="salesHistoryBody">
            {sales.map((s) => (
              <tr key={s.id} data-sale={s.doc_no}>
                <td>{formatDate(s.sold_at, lang)}</td>
                <td>
                  <b>{s.doc_no}</b>
                  <br />
                  <span className="tiny muted">{s.seller_name}</span>
                </td>
                <td className="num">
                  {formatNumber(s.units, lang)}
                  {s.returned_units > 0 ? <span className="tiny muted"> {t('salesHistory.returned', { n: s.returned_units })}</span> : null}
                </td>
                <td className="num">{formatMoney(s.total, lang)}</td>
                <td className="tiny">{s.payments.map((p) => `${p.name} ${formatMoney(p.amount, lang)}`).join(' + ')}</td>
                <td>
                  <Badge tone={s.status === 'ACTIVE' ? 'green' : 'red'}>{t(`docStatus.${s.status}`)}</Badge>
                </td>
                <td>
                  {can('sale.return') && s.status === 'ACTIVE' && s.returned_units < s.units ? (
                    <button type="button" className="btn sm" data-return={s.doc_no} onClick={() => open(s)}>
                      {t('returns.button')}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {sales.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty">
                  <EmptyState icon="receipt" title={t('salesHistory.empty')} hint={t('salesHistory.emptyHint')} />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Modal open={!!sale} title={t('returns.title', { doc: sale?.doc_no ?? '' })} help={t('returns.help')} onClose={() => setSale(null)}>
        {sale ? (
          <div className="formgrid" id="returnForm">
            <div className="span2 tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('returns.product')}</th>
                    <th>{t('returns.sold')}</th>
                    <th>{t('returns.alreadyReturned')}</th>
                    <th>{t('returns.toReturn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sale.lines.map((l) => (
                    <tr key={l.sale_line_id}>
                      <td>
                        <b>{l.ref}</b> {l.product_name}
                        <br />
                        <span className="tiny muted">{variantLabel(l)}</span>
                      </td>
                      <td>{l.qty}</td>
                      <td>{l.returned_qty}</td>
                      <td>
                        <input
                          className="input" type="number" min={0} max={l.qty - l.returned_qty} style={{ width: 90 }} data-return-line={l.sale_line_id}
                          value={qtys[l.sale_line_id] ?? ''} placeholder="0" onChange={(e) => setQtys({ ...qtys, [l.sale_line_id]: e.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="field">
              <label htmlFor="returnReason">{t('returns.reason')}</label>
              <input id="returnReason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="returnRefund">{t('returns.refund')}</label>
              <select id="returnRefund" value={refund} onChange={(e) => setRefund(e.target.value)}>
                <option value="">{t('returns.refundExchange')}</option>
                {methods.filter((m) => m.active && ['CASH', 'TRANSFER', 'CARD'].includes(m.kind)).map((m) => (
                  <option key={m.id} value={m.code}>
                    {m.name}
                  </option>
                ))}
              </select>
              <div className="help">{t('returns.refundHelp')}</div>
              {saleHadCash && refund === '' ? (
                <div className="help warn" id="returnNoRefundWarn">{t('returns.refundCashWarn')}</div>
              ) : null}
            </div>
            <button type="button" className="btn black span2" id="returnSubmit" disabled={busy} onClick={() => void submit()}>
              {t('returns.submit')}
            </button>
          </div>
        ) : null}
      </Modal>
    </Panel>
  );
}
